/**
 * Business Ops Worklog Modal
 * ==========================
 * "5 Pillars Update" header button and modal for:
 * - Daily work session tracking (start/end)
 * - Task selection
 * - Real-time comment thread
 * - End-of-day summary + screenshot upload
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Target, Play, Square, Clock, CheckCircle, Circle, MessageSquare,
  Upload, Image, Send, X, Loader2, AlertTriangle, ChevronDown, ChevronUp,
  FileText, Plus, ListChecks, Calendar, BarChart3, Trash2, Tag
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Hub definitions for work categorization
const HUBS = [
  { id: 'call_center', name: 'Call Center', color: 'blue' },
  { id: 'sales', name: 'Sales', color: 'green' },
  { id: 'operations', name: 'Operations', color: 'amber' },
  { id: 'marketing', name: 'Marketing', color: 'purple' },
  { id: 'platform', name: 'Platform/Core', color: 'slate' },
  { id: 'new_feature', name: 'New Hub/Feature', color: 'indigo' }
];

export const WorklogModal = ({ isOpen, onClose, currentUser }) => {
  const [loading, setLoading] = useState(true);
  const [activeSession, setActiveSession] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Manual work entry state
  const [manualEntries, setManualEntries] = useState([]);
  const [newEntry, setNewEntry] = useState({
    description: '',
    working_on_next: '',
    blockers: '',
    hub_id: '',
    is_mvp: false
  });
  const [addingEntry, setAddingEntry] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  
  // End day form
  const [endForm, setEndForm] = useState({
    summary: '',
    blockers: '',
    whats_next: '',
    tasks_completed_ids: [],
    attachment_ids: []
  });
  
  // File upload
  const [uploading, setUploading] = useState(false);
  const [attachments, setAttachments] = useState([]);

  const isBusinessOps = ['super_admin', 'admin', 'business_ops', 'operations_manager'].includes(currentUser?.role);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [sessionRes, tasksRes] = await Promise.all([
        axios.get(`${API}/five-pillars-hub/workday/my-active`, { headers: getHeaders() }).catch(() => ({ data: { session: null } })),
        axios.get(`${API}/tasks?limit=100`, { headers: getHeaders() }).catch(() => ({ data: [] }))
      ]);
      
      setActiveSession(sessionRes.data.session);
      
      // Filter tasks - show pending/in_progress tasks
      const allTasks = Array.isArray(tasksRes.data) ? tasksRes.data : (tasksRes.data.tasks || []);
      const activeTasks = allTasks.filter(t => 
        t.status !== 'completed' && t.status !== 'cancelled'
      );
      setTasks(activeTasks);
      
      if (sessionRes.data.session) {
        setSelectedTaskIds(sessionRes.data.session.selected_task_ids || []);
        setComments(sessionRes.data.session.comments || []);
        setManualEntries(sessionRes.data.session.manual_entries || []);
      } else {
        setManualEntries([]);
      }
    } catch (error) {
      console.error('Error fetching worklog data:', error);
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    if (isOpen) {
      fetchData();
      // Poll for updates every 30 seconds
      const interval = setInterval(fetchData, 30000);
      return () => clearInterval(interval);
    }
  }, [isOpen, fetchData]);

  // Start day
  const startDay = async () => {
    try {
      setSubmitting(true);
      await axios.post(
        `${API}/five-pillars-hub/workday/start`,
        { selected_task_ids: selectedTaskIds },
        { headers: getHeaders() }
      );
      toast.success('Work day started!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start day');
    } finally {
      setSubmitting(false);
    }
  };

  // Update selected tasks
  const updateSelectedTasks = async (taskIds) => {
    setSelectedTaskIds(taskIds);
    if (activeSession) {
      try {
        await axios.patch(
          `${API}/five-pillars-hub/workday/select-tasks`,
          { selected_task_ids: taskIds },
          { headers: getHeaders() }
        );
      } catch (error) {
        console.error('Error updating tasks:', error);
      }
    }
  };

  // Toggle task selection
  const toggleTask = (taskId) => {
    const newSelection = selectedTaskIds.includes(taskId)
      ? selectedTaskIds.filter(id => id !== taskId)
      : [...selectedTaskIds, taskId];
    updateSelectedTasks(newSelection);
  };

  // Add manual work entry
  const addManualEntry = async () => {
    if (!newEntry.description.trim() && !newEntry.working_on_next.trim() && !newEntry.blockers.trim()) {
      toast.error('Please fill in at least one field');
      return;
    }
    
    if (!activeSession) {
      toast.error('Start your work day first before adding entries');
      return;
    }
    
    try {
      setAddingEntry(true);
      // Combine all fields into a structured description
      let description = '';
      if (newEntry.description.trim()) {
        description += `✓ Completed: ${newEntry.description.trim()}`;
      }
      if (newEntry.working_on_next.trim()) {
        description += description ? '\n' : '';
        description += `→ Next: ${newEntry.working_on_next.trim()}`;
      }
      if (newEntry.blockers.trim()) {
        description += description ? '\n' : '';
        description += `⚠ Blocker: ${newEntry.blockers.trim()}`;
      }
      
      await axios.post(
        `${API}/five-pillars-hub/workday/manual-entry`,
        {
          description: description,
          hub_id: newEntry.hub_id || null,
          custom_hub_name: null,
          is_mvp: newEntry.is_mvp
        },
        { headers: getHeaders() }
      );
      toast.success('Update added');
      setNewEntry({ description: '', working_on_next: '', blockers: '', hub_id: '', is_mvp: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add entry');
    } finally {
      setAddingEntry(false);
    }
  };

  // Create a quick task
  const createQuickTask = async () => {
    if (!newTaskTitle.trim()) {
      toast.error('Please enter a task title');
      return;
    }
    
    try {
      const taskData = {
        title: newTaskTitle.trim(),
        priority: 'medium',
        status: 'pending',
        tags: newEntry.hub_id ? [`hub:${newEntry.hub_id}`] : []
      };
      
      if (newEntry.is_mvp) {
        taskData.tags.push('mvp');
      }
      
      await axios.post(`${API}/tasks`, taskData, { headers: getHeaders() });
      toast.success('Task created');
      setNewTaskTitle('');
      setShowAddTask(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  // Delete manual work entry
  const deleteManualEntry = async (entryId) => {
    try {
      await axios.delete(
        `${API}/five-pillars-hub/workday/manual-entry/${entryId}`,
        { headers: getHeaders() }
      );
      toast.success('Entry removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove entry');
    }
  };

  // End day
  const endDay = async () => {
    try {
      setSubmitting(true);
      await axios.post(
        `${API}/five-pillars-hub/workday/end`,
        {
          ...endForm,
          attachment_ids: attachments.map(a => a.id)
        },
        { headers: getHeaders() }
      );
      toast.success('Work day ended!');
      setShowEndDialog(false);
      setEndForm({ summary: '', blockers: '', whats_next: '', tasks_completed_ids: [], attachment_ids: [] });
      setAttachments([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to end day');
    } finally {
      setSubmitting(false);
    }
  };

  // Add comment
  const addComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      await axios.post(
        `${API}/five-pillars-hub/workday/comments`,
        { content: newComment },
        { headers: getHeaders() }
      );
      setNewComment('');
      fetchData();
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  // Upload attachment
  const uploadAttachment = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/assets/upload`,
        formData,
        { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      
      setAttachments(prev => [...prev, response.data]);
      toast.success('Screenshot uploaded');
    } catch (error) {
      toast.error('Failed to upload screenshot');
    } finally {
      setUploading(false);
    }
  };

  // Calculate live duration
  const getLiveDuration = () => {
    if (!activeSession?.started_at) return null;
    const start = new Date(activeSession.started_at);
    const now = new Date();
    const diff = now - start;
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${activeSession ? 'bg-green-100 dark:bg-green-900/30' : 'bg-slate-100 dark:bg-slate-800'}`}>
                  <Target className={`h-5 w-5 ${activeSession ? 'text-green-600' : 'text-slate-500'}`} />
                </div>
                <div>
                  <DialogTitle>5 Pillars Update</DialogTitle>
                  <DialogDescription>
                    {activeSession ? 'Add to your day\'s update' : 'Start your work day'}
                  </DialogDescription>
                </div>
              </div>
              {activeSession && (
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-green-500 animate-pulse" />
                  <Badge className="bg-green-600">Working</Badge>
                  <Badge variant="outline">
                    <Clock className="h-3 w-3 mr-1" />
                    {getLiveDuration()}
                  </Badge>
                </div>
              )}
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {/* Quick Add Section - Only when clocked in */}
            {activeSession && isBusinessOps && (
              <div className="border-2 border-green-200 dark:border-green-800 rounded-lg p-4 bg-green-50 dark:bg-green-900/20">
                <Label className="text-sm font-semibold mb-3 flex items-center gap-2 text-green-700 dark:text-green-300">
                  <Plus className="h-4 w-4" />
                  Add to Today&apos;s Update
                </Label>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* What I Finished */}
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">What I Finished</Label>
                    <Textarea
                      placeholder="Tasks completed..."
                      value={newEntry.description}
                      onChange={(e) => setNewEntry(prev => ({ ...prev, description: e.target.value }))}
                      className="h-16 bg-white dark:bg-slate-900 text-sm"
                    />
                  </div>
                  
                  {/* What I'm Working On Next */}
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Working On Next</Label>
                    <Textarea
                      placeholder="Next tasks..."
                      value={newEntry.working_on_next}
                      onChange={(e) => setNewEntry(prev => ({ ...prev, working_on_next: e.target.value }))}
                      className="h-16 bg-white dark:bg-slate-900 text-sm"
                    />
                  </div>
                  
                  {/* Blockers */}
                  <div>
                    <Label className="text-xs text-red-600 dark:text-red-400 mb-1 block flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Blockers
                    </Label>
                    <Textarea
                      placeholder="Any blockers..."
                      value={newEntry.blockers}
                      onChange={(e) => setNewEntry(prev => ({ ...prev, blockers: e.target.value }))}
                      className="h-16 bg-white dark:bg-slate-900 text-sm border-red-200 dark:border-red-800"
                    />
                  </div>
                </div>
                
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-3">
                    <Select
                      value={newEntry.hub_id}
                      onValueChange={(value) => setNewEntry(prev => ({ ...prev, hub_id: value }))}
                    >
                      <SelectTrigger className="w-32 h-8 bg-white dark:bg-slate-900 text-xs">
                        <SelectValue placeholder="Hub" />
                      </SelectTrigger>
                      <SelectContent>
                        {HUBS.map(hub => (
                          <SelectItem key={hub.id} value={hub.id}>
                            {hub.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <div className="flex items-center gap-1">
                      <Checkbox
                        id="mvp-tag-quick"
                        checked={newEntry.is_mvp}
                        onCheckedChange={(checked) => setNewEntry(prev => ({ ...prev, is_mvp: checked }))}
                      />
                      <Label htmlFor="mvp-tag-quick" className="text-xs cursor-pointer">MVP</Label>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {/* Add Task Button */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowAddTask(!showAddTask)}
                      className="text-xs"
                    >
                      <ListChecks className="h-3 w-3 mr-1" />
                      Add Task
                    </Button>
                    
                    <Button
                      size="sm"
                      onClick={addManualEntry}
                      disabled={addingEntry || (!newEntry.description.trim() && !newEntry.working_on_next.trim() && !newEntry.blockers.trim())}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {addingEntry ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4 mr-1" />
                      )}
                      Add Update
                    </Button>
                  </div>
                </div>
                
                {/* Quick Add Task */}
                {showAddTask && (
                  <div className="mt-3 p-3 bg-white dark:bg-slate-900 rounded-lg border">
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="Task title..."
                        value={newTaskTitle}
                        onChange={(e) => setNewTaskTitle(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && createQuickTask()}
                        className="flex-1 h-8 text-sm"
                      />
                      <Button size="sm" onClick={createQuickTask} disabled={!newTaskTitle.trim()}>
                        Create
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setShowAddTask(false)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Task will be tagged with selected hub {newEntry.is_mvp && '& MVP'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Today's Updates Log */}
            {activeSession && manualEntries.length > 0 && (
              <div>
                <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Today&apos;s Updates ({manualEntries.length})
                </Label>
                <ScrollArea className="h-32 border rounded-lg p-2">
                  <div className="space-y-2">
                    {manualEntries.map(entry => (
                      <div key={entry.id} className="flex items-start gap-2 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium dark:text-white">{entry.description}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {entry.hub_name && (
                              <Badge variant="secondary" className="text-xs">
                                {entry.hub_name}
                              </Badge>
                            )}
                            {entry.is_mvp && (
                              <Badge className="text-xs bg-orange-500 hover:bg-orange-600">
                                <Tag className="h-3 w-3 mr-1" />
                                MVP
                              </Badge>
                            )}
                            <span className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(entry.created_at), { addSuffix: true })}
                            </span>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteManualEntry(entry.id)}
                          className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}

            <Separator />

            {/* Task Selection */}
            <div>
              <Label className="text-sm font-medium mb-2 block">
                Today&apos;s Build Tasks
                {selectedTaskIds.length > 0 && (
                  <Badge variant="secondary" className="ml-2">{selectedTaskIds.length} selected</Badge>
                )}
              </Label>
              <ScrollArea className="h-48 border rounded-lg p-2">
                {tasks.length > 0 ? (
                  <div className="space-y-2">
                    {tasks.map(task => (
                      <div
                        key={task.id}
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                          selectedTaskIds.includes(task.id)
                            ? 'bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800'
                            : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                        }`}
                        onClick={() => isBusinessOps && toggleTask(task.id)}
                      >
                        <Checkbox
                          checked={selectedTaskIds.includes(task.id)}
                          disabled={!isBusinessOps}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate dark:text-white">{task.title}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <Badge variant="outline" className="text-xs">
                              {task.priority || 'medium'}
                            </Badge>
                            {task.tags?.filter(t => t.startsWith('hub:')).map(tag => (
                              <Badge key={tag} variant="secondary" className="text-xs">
                                {tag.split(':')[1]}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <ListChecks className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No active tasks found</p>
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Work Day Controls (Business Ops only) */}
            {isBusinessOps && (
              <div className="flex gap-3">
                {!activeSession ? (
                  <Button
                    onClick={startDay}
                    disabled={submitting}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {submitting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="h-4 w-4 mr-2" />
                    )}
                    Start Day
                  </Button>
                ) : (
                  <Button
                    variant="destructive"
                    onClick={() => setShowEndDialog(true)}
                    disabled={submitting}
                  >
                    <Square className="h-4 w-4 mr-2" />
                    End Day
                  </Button>
                )}
              </div>
            )}

            <Separator />

            {/* Comments Thread */}
            <div>
              <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Discussion Thread
                {comments.length > 0 && <Badge variant="secondary">{comments.length}</Badge>}
              </Label>
              
              <ScrollArea className="h-40 border rounded-lg p-3 mb-3">
                {comments.length > 0 ? (
                  <div className="space-y-3">
                    {comments.map(comment => (
                      <div key={comment.id} className="flex gap-2">
                        <Avatar className="h-7 w-7 flex-shrink-0">
                          <AvatarFallback className="text-xs">
                            {comment.user_name?.charAt(0) || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium dark:text-white">
                              {comment.user_name}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 dark:text-slate-300 mt-0.5">
                            {comment.content}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-muted-foreground">
                    <MessageSquare className="h-6 w-6 mx-auto mb-2 opacity-50" />
                    <p className="text-xs">No comments yet</p>
                  </div>
                )}
              </ScrollArea>
              
              {activeSession && (
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addComment()}
                    className="flex-1"
                  />
                  <Button size="sm" onClick={addComment} disabled={!newComment.trim()}>
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* End Day Dialog */}
      <Dialog open={showEndDialog} onOpenChange={setShowEndDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>End Work Day</DialogTitle>
            <DialogDescription>
              Summarize what you accomplished today
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>What did you complete today? *</Label>
              <Textarea
                placeholder="Describe what you accomplished..."
                value={endForm.summary}
                onChange={(e) => setEndForm(prev => ({ ...prev, summary: e.target.value }))}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Blockers (if any)</Label>
              <Textarea
                placeholder="Any blockers or issues..."
                value={endForm.blockers}
                onChange={(e) => setEndForm(prev => ({ ...prev, blockers: e.target.value }))}
                className="mt-1 h-20"
              />
            </div>
            
            <div>
              <Label>What&apos;s next?</Label>
              <Textarea
                placeholder="What's planned for tomorrow..."
                value={endForm.whats_next}
                onChange={(e) => setEndForm(prev => ({ ...prev, whats_next: e.target.value }))}
                className="mt-1 h-20"
              />
            </div>

            {/* Mark tasks as completed */}
            {selectedTaskIds.length > 0 && (
              <div>
                <Label className="text-sm mb-2 block">Mark tasks as completed</Label>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {tasks.filter(t => selectedTaskIds.includes(t.id)).map(task => (
                    <div key={task.id} className="flex items-center gap-2">
                      <Checkbox
                        checked={endForm.tasks_completed_ids.includes(task.id)}
                        onCheckedChange={(checked) => {
                          setEndForm(prev => ({
                            ...prev,
                            tasks_completed_ids: checked
                              ? [...prev.tasks_completed_ids, task.id]
                              : prev.tasks_completed_ids.filter(id => id !== task.id)
                          }));
                        }}
                      />
                      <span className="text-sm truncate dark:text-white">{task.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Screenshot Upload */}
            <div>
              <Label className="text-sm mb-2 flex items-center gap-2">
                <Image className="h-4 w-4" />
                Attach Screenshots
              </Label>
              <div className="flex flex-wrap gap-2">
                {attachments.map((att, idx) => (
                  <div key={idx} className="relative w-16 h-16 rounded border overflow-hidden">
                    <img src={att.url || att.thumbnail_url} alt="" className="w-full h-full object-cover" />
                    <button
                      className="absolute top-0 right-0 bg-red-500 text-white p-0.5 rounded-bl"
                      onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <label className="w-16 h-16 border-2 border-dashed rounded flex items-center justify-center cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800">
                  {uploading ? (
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  ) : (
                    <Upload className="h-5 w-5 text-muted-foreground" />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={uploadAttachment}
                    disabled={uploading}
                  />
                </label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEndDialog(false)}>Cancel</Button>
            <Button
              onClick={endDay}
              disabled={!endForm.summary.trim() || submitting}
              className="bg-green-600 hover:bg-green-700"
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <CheckCircle className="h-4 w-4 mr-2" />
              Complete Day
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * Header Button Component
 * For use in Layout.js header
 */
export const WorklogHeaderButton = ({ currentUser, onClick }) => {
  const [liveStatus, setLiveStatus] = useState(null);
  
  // Only admin roles can see the 5 Pillars Update button
  const isAdmin = ['super_admin', 'admin'].includes(currentUser?.role);

  useEffect(() => {
    if (!isAdmin) return;
    
    const fetchStatus = async () => {
      try {
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        const response = await axios.get(
          `${API}/five-pillars-hub/workday/live-status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setLiveStatus(response.data);
      } catch (error) {
        console.error('Error fetching live status:', error);
      }
    };
    
    fetchStatus();
    const interval = setInterval(fetchStatus, 60000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  if (!isAdmin) return null;

  return (
    <Button
      variant={liveStatus?.is_working ? "default" : "outline"}
      size="sm"
      onClick={onClick}
      className={liveStatus?.is_working ? 'bg-green-600 hover:bg-green-700' : ''}
      data-testid="worklog-header-btn"
    >
      {liveStatus?.is_working && (
        <div className="h-2 w-2 rounded-full bg-white animate-pulse mr-2" />
      )}
      <Target className="h-4 w-4 mr-1" />
      5 Pillars Update
    </Button>
  );
};

export default WorklogModal;
